import type { CompanyValidationErrors } from '@/scripts/company-data';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type CompanyValidationErrorsCardProps = {
  validationErrors: CompanyValidationErrors;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
};

export function CompanyValidationErrorsCard({
  validationErrors,
  className,
}: CompanyValidationErrorsCardProps) {
  const { total, entries } = validationErrors;

  if (!entries.length) {
    return null;
  }

  const totalLabel = total === 1 ? '1 error total' : `${total} errors total`;

  return (
    <Card variant="grid" className={className}>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-base">File Validation Errors</CardTitle>
        <Badge variant={total > 0 ? 'destructive' : 'secondary'}>
          {totalLabel}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-6">
        {entries.map((entry, index) => (
          <section
            key={`${entry.year ?? 'unknown'}-${index}`}
            className="space-y-4 rounded-2xl border bg-muted/40 p-4"
          >
            <div className="flex items-center justify-between border-b pb-2">
              <p className="text-sm font-bold text-foreground">
                {entry.year ?? 'Year not specified'}
              </p>
              <Badge variant="outline" className="font-mono text-[10px]">
                {entry.errorCount} {entry.errorCount === 1 ? 'ISSUE' : 'ISSUES'}
              </Badge>
            </div>

            <div className="space-y-4">
              {entry.errors.map((error, errorIndex) => (
                <div
                  key={errorIndex}
                  className="space-y-2 last:border-0 border-b pb-3 last:pb-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={
                        error.severity === 'CRITICAL'
                          ? 'destructive'
                          : 'secondary'
                      }
                      className="text-[10px] h-5"
                    >
                      {error.severity}
                    </Badge>
                    <span className="text-xs font-semibold text-foreground uppercase tracking-tight">
                      {error.errorType}
                    </span>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {error.message}
                  </p>

                  {error.details && (
                    <div className="mt-2 rounded bg-black/5 dark:bg-white/10 p-2 overflow-x-auto">
                      <pre className="text-[10px] font-mono leading-tight text-foreground/70">
                        {JSON.stringify(error.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  );
}
